import { FormEvent, useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { Panel } from "../components/Panel";
import {
  fetchAdminUsers,
  generatePasswordSetupLink,
  sendPasswordSetupLink,
  updateAdminUserEmail,
  type AdminUser,
  type SetupLinkResult
} from "../lib/api/adminUsersClient";
import type { Role } from "../types";

const roleLabels: Record<Role, string> = {
  hauffe_admin: "HAUFFE Admin",
  company_owner: "Company Owner",
  department_contributor: "Department Contributor"
};

export function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [emailDrafts, setEmailDrafts] = useState<Record<string, string>>({});
  const [setupLinks, setSetupLinks] = useState<Record<string, SetupLinkResult>>({});
  const [notice, setNotice] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState<string | undefined>();

  useEffect(() => {
    void loadUsers();
  }, []);

  async function loadUsers() {
    setIsLoading(true);
    setError(undefined);
    try {
      const loadedUsers = await fetchAdminUsers();
      setUsers(loadedUsers);
      setEmailDrafts(Object.fromEntries(loadedUsers.map((user) => [user.id, user.email])));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Nutzer konnten nicht geladen werden.");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleEmailUpdate(event: FormEvent<HTMLFormElement>, userId: string) {
    event.preventDefault();
    setBusyUserId(userId);
    setNotice(undefined);
    setError(undefined);
    try {
      const updated = await updateAdminUserEmail(userId, emailDrafts[userId] ?? "");
      setUsers((current) => current.map((user) => (user.id === updated.id ? updated : user)));
      setEmailDrafts((current) => ({ ...current, [updated.id]: updated.email }));
      setNotice("E-Mail-Adresse wurde aktualisiert. Rolle und Zuordnung blieben unverändert.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "E-Mail-Adresse konnte nicht aktualisiert werden.");
    } finally {
      setBusyUserId(undefined);
    }
  }

  async function handleGenerateSetupLink(userId: string) {
    setBusyUserId(userId);
    setNotice(undefined);
    setError(undefined);
    try {
      const result = await generatePasswordSetupLink(userId);
      setSetupLinks((current) => ({ ...current, [userId]: result }));
      setNotice("Passwort-Setup-Link wurde erzeugt und kann kopiert werden.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Setup-Link konnte nicht erzeugt werden.");
    } finally {
      setBusyUserId(undefined);
    }
  }

  async function handleSendSetupLink(userId: string) {
    setBusyUserId(userId);
    setNotice(undefined);
    setError(undefined);
    try {
      const result = await sendPasswordSetupLink(userId);
      setSetupLinks((current) => ({ ...current, [userId]: result }));
      if (result.emailSent) {
        setNotice("Passwort-Setup-Link wurde per E-Mail versendet.");
      } else {
        setNotice(result.emailError ?? "E-Mail-Versand war nicht möglich. Der Setup-Link bleibt kopierbar.");
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Setup-Link konnte nicht versendet werden.");
    } finally {
      setBusyUserId(undefined);
    }
  }

  async function handleCopy(link: string) {
    await navigator.clipboard.writeText(link);
    setNotice("Setup-Link wurde kopiert.");
  }

  return (
    <Layout
      eyebrow="HAUFFE Admin"
      title="Nutzerverwaltung"
      description="Benutzerkonten aktivieren, E-Mail-Adressen pflegen und sichere Passwort-Setup-Links erzeugen."
    >
      {notice ? (
        <Panel title="Status" variant="soft">
          <p>{notice}</p>
        </Panel>
      ) : null}
      {error ? (
        <Panel title="Hinweis" variant="soft">
          <p className="form-error" role="alert">{error}</p>
        </Panel>
      ) : null}

      <Panel title="Nutzerliste" variant="accent">
        <p className="form-note">
          Passwörter werden nicht sichtbar gemacht. Setup-Links sind einmalig verwendbar und 24 Stunden gültig.
        </p>
        {isLoading ? <p>Nutzer werden geladen...</p> : null}
        {!isLoading && users.length === 0 ? <p>Es wurden noch keine Nutzer gefunden.</p> : null}

        <div className="user-management-list">
          {users.map((user) => {
            const setupLink = setupLinks[user.id];
            return (
              <article className="user-management-card" key={user.id}>
                <div className="user-management-header">
                  <div>
                    <strong>{user.email}</strong>
                    <span>{user.name || "Kein Name hinterlegt"}</span>
                  </div>
                  <span className={`status-badge status-${user.isActive ? "submitted" : "locked"}`}>
                    {user.isActive ? "Aktiv" : "Inaktiv"}
                  </span>
                </div>

                <dl className="user-management-meta">
                  <div>
                    <dt>Rolle</dt>
                    <dd>{roleLabels[user.role]}</dd>
                  </div>
                  <div>
                    <dt>Passwort gesetzt</dt>
                    <dd>{user.hasPassword ? "Ja" : "Nein"}</dd>
                  </div>
                  <div>
                    <dt>password_set_at</dt>
                    <dd>{user.passwordSetAt ? new Date(user.passwordSetAt).toLocaleString("de-DE") : "Nicht gesetzt"}</dd>
                  </div>
                </dl>

                <form className="inline-form" onSubmit={(event) => handleEmailUpdate(event, user.id)}>
                  <label>
                    E-Mail-Adresse ändern
                    <input
                      type="email"
                      value={emailDrafts[user.id] ?? user.email}
                      onChange={(event) =>
                        setEmailDrafts((current) => ({ ...current, [user.id]: event.target.value }))
                      }
                      required
                    />
                  </label>
                  <button className="button button-secondary" type="submit" disabled={busyUserId === user.id}>
                    E-Mail speichern
                  </button>
                </form>

                <div className="admin-actions">
                  <button
                    className="button button-secondary"
                    type="button"
                    onClick={() => handleGenerateSetupLink(user.id)}
                    disabled={busyUserId === user.id}
                  >
                    Setup-Link erzeugen
                  </button>
                  <button
                    className="button button-primary"
                    type="button"
                    onClick={() => handleSendSetupLink(user.id)}
                    disabled={busyUserId === user.id}
                  >
                    Setup-Link per E-Mail senden
                  </button>
                </div>

                {setupLink ? (
                  <div className="setup-link-box">
                    <span>Gültig bis {new Date(setupLink.expiresAt).toLocaleString("de-DE")}</span>
                    <code>{setupLink.setupLink}</code>
                    <button className="button button-secondary" type="button" onClick={() => handleCopy(setupLink.setupLink)}>
                      Link kopieren
                    </button>
                    {setupLink.emailError ? <p className="form-note">{setupLink.emailError}</p> : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </Panel>
    </Layout>
  );
}
